import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import './Registro.css';

const Registro = () => {
  const [identification, setIdentification] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState({});
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para el bloqueo del botón
  const navigate = useNavigate();
  const toast = useRef(null);

  // Función de validación de la cédula
  const validateCedula = (cedula) => {
    if (!/^\d{10}$/.test(cedula)) {
      return false; // La cédula debe tener exactamente 10 dígitos numéricos
    }

    const provincia = parseInt(cedula.slice(0, 2), 10);
    const tercerDigito = parseInt(cedula[2], 10);

    if (provincia < 0 || provincia > 24 || tercerDigito > 5) {
      return false;
    }

    const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    const digitos = cedula.split('').map(Number);
    const digitoVerificador = digitos.pop();

    let suma = 0;

    for (let i = 0; i < coeficientes.length; i++) {
      let producto = digitos[i] * coeficientes[i];
      if (producto >= 10) {
        producto -= 9;
      }
      suma += producto;
    }

    const modulo = suma % 10;
    const resultado = modulo === 0 ? 0 : 10 - modulo;
    return resultado === digitoVerificador;
  };

  // Función de validación del email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función de validación de los campos
  const validateFields = () => {
    const errors = {};

    if (!identification) {
      errors.identification = 'El campo de cédula es obligatorio.';
    } else if (!validateCedula(identification)) {
      errors.identification = 'La cédula ingresada no es válida.';
    }

    if (!email) {
      errors.email = 'El campo de email es obligatorio.';
    } else if (!validateEmail(email)) {
      errors.email = 'El email ingresado no es válido.';
    }

    if (!firstName) {
      errors.firstName = 'El campo de nombre es obligatorio.';
    }

    if (!lastName) {
      errors.lastName = 'El campo de apellido es obligatorio.';
    }

    if (!password) {
      errors.password = 'El campo de contraseña es obligatorio.';
    } else if (password.length < 8) {
      errors.password = 'La contraseña debe tener al menos 8 caracteres.';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Debe confirmar su contraseña.';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden.';
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  // Función para manejar el registro
  const handleRegister = () => {
    setError({});
    setSuccess('');
    setIsSubmitting(true); // Bloquea el botón

    if (!validateFields()) {
      setIsSubmitting(false); // Desbloquea el botón si hay errores
      return;
    }

    const requestBody = {
      username: identification,
      email,
      firstName,
      lastName,
      password,
      confirmPassword,
    };

    fetch('/api/security/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(text || 'Error al registrar el usuario');
          });
        }
        return response.json().catch(() => {
          return {}; // Devolver un objeto vacío si la respuesta no es JSON
        });
      })
      .then(data => {
        setIdentification('');
        setEmail('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setConfirmPassword('');

        setSuccess('Usuario registrado exitosamente');
        setError({});
        toast.current.show({ severity: 'success', summary: 'Registrado', detail: 'Usted se ha registrado correctamente', life: 6000 });
        setTimeout(() => {
          setIsSubmitting(false); // Desbloquea el botón
          navigate('/'); // Redirige a la página de inicio (login)
        }, 3000);
      })
      .catch(error => {
        if (error.message.includes('usuario ya existe')) {
          toast.current.show({ severity: 'error', summary: 'Error', detail: 'El usuario ya existe', life: 6000 });
        } else {
          toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al registrar el usuario', life: 6000 });
        }
        setIsSubmitting(false); // Desbloquea el botón en caso de error
      });
  };

  return (
    <div className="register-container">
      <Toast ref={toast} position="top-center" />
      <Card className="register-card">
        <h2 className="register-title">Registro de Vendedores</h2>
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="identification">Número de Cédula</label>
            <InputText
              id="identification"
              value={identification}
              onChange={(e) => setIdentification(e.target.value)}
              className={error.identification ? 'p-invalid' : ''}
              disabled={isSubmitting}
            />
            {error.identification && <small className="p-error">{error.identification}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={error.email ? 'p-invalid' : ''}
              disabled={isSubmitting}
            />
            {error.email && <small className="p-error">{error.email}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="firstName">Nombre</label>
            <InputText
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={error.firstName ? 'p-invalid' : ''}
              disabled={isSubmitting}
            />
            {error.firstName && <small className="p-error">{error.firstName}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="lastName">Apellido</label>
            <InputText
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={error.lastName ? 'p-invalid' : ''}
              disabled={isSubmitting}
            />
            {error.lastName && <small className="p-error">{error.lastName}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="password">Contraseña</label>
            <Password
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              feedback={false}
              toggleMask
              className={error.password ? 'p-invalid' : ''}
              disabled={isSubmitting}
              placeholder="Ingrese una contraseña"
            />
            {error.password && <small className="p-error">{error.password}</small>}
          </div>
          <div className="p-field">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <Password
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              feedback={false}
              toggleMask
              className={error.confirmPassword ? 'p-invalid' : ''}
              disabled={isSubmitting}
              placeholder="Confirme su contraseña"

            />
            {error.confirmPassword && <small className="p-error">{error.confirmPassword}</small>}
          </div>
          <Button
            label="Regresar"
            onClick={() => navigate('/')}
            className="p-button-rounded p-button-lg p-button-secondary p-button-raised p-button-text"
            disabled={isSubmitting} // Deshabilitar mientras se envía
          />
          <Button
            label="Registrar"
            icon="pi pi-user-plus"
            onClick={handleRegister}
            disabled={isSubmitting}
          />
          {success && <p className="p-success">{success}</p>}
        </div>
      </Card>
    </div>
  );
};

export default Registro;
